/**
 * Target dossier panel.
 *
 * Two states:
 *   - UNKNOWN: no safehouse visited this round yet. Shows a silhouette
 *     placeholder with a prompt. Silent — no hints about what the
 *     agent is carrying.
 *   - REVEALED: player has entered a safehouse this round. Shows the
 *     full dossier (face photo, codename, archetype, weapon, defensive
 *     items, passives, and a card-type breakdown of the agent's deck).
 *
 * Reveal flips once the player enters their first safehouse for the
 * round (see handleEnterSafehouse in GameScreen.tsx). Resets to false
 * each round via initializeGameState building a fresh GameState.
 */
import React from 'react';
import { theme } from '../../styles/theme';
import type { Agent, Card } from '../../types/game';
import { ITEMS } from '../../data/items';

// Build a by-id lookup so `weaponId: 'sniper-rifle'` / `defensiveItemIds: ['flashbang']`
// can resolve to their item templates for the description hover. ITEMS is keyed in
// camelCase but its item.id values are the kebab-case strings the agent loadout uses.
const ITEMS_BY_ID = Object.fromEntries(
  Object.values(ITEMS).map(item => [item.id, item]),
) as Record<string, import('../../types/game').Item>;

interface TargetPanelProps {
  agent: Agent;
  revealed: boolean;
}

export function TargetPanel({ agent, revealed }: TargetPanelProps) {
  if (!revealed) return <UnknownDossier beliefConfidence={agent.belief.confidence} />;
  return <RevealedDossier agent={agent} />;
}

// --- Unknown state ---

function UnknownDossier({ beliefConfidence }: { beliefConfidence: number }) {
  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.headerLabel}>TARGET</span>
      </div>
      <div style={styles.unknownBody}>
        <div style={styles.silhouette}>?</div>
        <div style={styles.unknownHint}>
          Visit a safehouse for intel.
        </div>
      </div>
      <BeliefBar confidence={beliefConfidence} />
    </div>
  );
}

// --- Belief bar (shared between unknown + revealed states) ---

/**
 * Horizontal bar showing how confident the agent is in the player's
 * current position (0 = no clue, 1 = visually confirmed). Color ramps
 * from green (low threat) to red (agent knows exactly where you are).
 * Hover reveals an explanation of what this represents.
 *
 * Hover handling uses React state rather than the class-based CSS rule
 * the rest of TargetPanel uses, because this bar renders in the
 * UnknownDossier path too (where that rule's <style> block isn't
 * present). State-based hover stays self-contained either way.
 */
function BeliefBar({ confidence }: { confidence: number }) {
  const [hovered, setHovered] = React.useState(false);
  const pct = Math.max(0, Math.min(1, confidence));
  const color =
    pct < 0.25 ? '#44cc88' :
    pct < 0.50 ? '#88cc44' :
    pct < 0.75 ? '#ffaa44' :
    '#ff5555';
  return (
    <div
      style={styles.beliefSection}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.beliefRow}>
        <span style={styles.beliefLabel}>BELIEF</span>
        <span style={{ ...styles.beliefPct, color }}>{Math.round(pct * 100)}%</span>
      </div>
      <div style={styles.beliefTrack}>
        <div style={{ ...styles.beliefFill, width: `${pct * 100}%`, background: color, boxShadow: `0 0 6px ${color}66` }} />
      </div>
      {hovered && (
        <span style={{ ...styles.beliefTip, borderColor: `${color}80` }}>
          <span style={{ ...styles.tipTitle, color }}>AGENT BELIEF — {Math.round(pct * 100)}%</span>
          <span style={styles.tipBody}>
            How confident the agent is in THEIR current guess at your position — not a measure of how right they are. High belief means they'll commit hard to their target; misdirection cards, decoys, Stage Exit, and Subway Surfer can let them lock on confidently to somewhere you aren't. Movement raises belief, hiding and stale intel lower it.
          </span>
        </span>
      )}
    </div>
  );
}

// --- Revealed state ---

function RevealedDossier({ agent }: { agent: Agent }) {
  const { loadout } = agent;
  const faceSrc = `./agent_faces/face_${String(loadout.faceId).padStart(2, '0')}.png`;

  // Count card types in the agent's full deck (deck + hand + discard) for
  // the "composition" breakdown. Per design decision: show category counts
  // rather than individual card list so the player gets strategic info
  // without spoiling specific plays.
  const fullDeck: Card[] = [...agent.deck, ...agent.hand, ...agent.discard];
  const counts: Record<string, number> = {};
  for (const card of fullDeck) {
    counts[card.category] = (counts[card.category] ?? 0) + 1;
  }
  const orderedCategories: Array<{ key: string; label: string; color: string }> = [
    { key: 'movement',   label: 'Movement',   color: '#66ccff' },
    { key: 'intel',      label: 'Intel',      color: '#aa88ff' },
    { key: 'tactics',    label: 'Tactics',    color: '#ffaa44' },
    { key: 'combat',     label: 'Combat',     color: '#ff5555' },
    { key: 'fieldcraft', label: 'Fieldcraft', color: '#44cc88' },
  ];

  // Weapon template (null for unarmed). Used both for the display label and
  // for the hover tooltip description.
  const weaponItem = loadout.weaponId ? ITEMS_BY_ID[loadout.weaponId] ?? null : null;
  const weaponLabel = weaponItem?.name ?? 'Unarmed';

  // Defensive item templates — resolve each id up front so we have names +
  // descriptions for the per-item hover tooltips.
  const defensiveItems = loadout.defensiveItemIds
    .map(id => ITEMS_BY_ID[id])
    .filter((i): i is import('../../types/game').Item => Boolean(i));

  // Hover tooltip rules (.target-panel-hover / .target-panel-tip) live in
  // src/styles/global.css so they apply regardless of whether the panel is
  // in its revealed or unknown state. Previously those rules were in an
  // inline <style> block here, which introduced timing issues that left
  // tooltips stuck visible on the left edge of the screen.
  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.headerLabel}>TARGET</span>
        <span style={styles.archetypeTag}>{loadout.archetype.toUpperCase()}</span>
      </div>
      <div style={styles.revealedBody}>
        <div style={styles.faceWrap}>
          <img src={faceSrc} alt={loadout.codename} style={styles.faceImg} />
        </div>
        <div style={styles.infoCol}>
          <div style={styles.codename}>"{loadout.codename}"</div>
          <div style={styles.kvRow}>
            <span style={styles.kvLabel}>WPN</span>
            <span
              className="target-panel-hover"
              style={{
                ...styles.kvValue,
                color: loadout.weaponId ? '#ff8844' : '#888',
                cursor: weaponItem ? 'help' : 'default',
              }}
            >
              {weaponLabel}
              {weaponItem?.description && (
                <span className="target-panel-tip" style={{ ...styles.tip, borderColor: 'rgba(255, 136, 68, 0.5)' }}>
                  <span style={{ ...styles.tipTitle, color: '#ff8844' }}>{weaponItem.name}</span>
                  <span style={styles.tipBody}>{weaponItem.description}</span>
                </span>
              )}
            </span>
          </div>
          {defensiveItems.length > 0 && (
            <div style={styles.kvRow}>
              <span style={styles.kvLabel}>GEAR</span>
              <span style={{ ...styles.kvValue, color: '#f0c040', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {defensiveItems.map((item, i) => (
                  <span
                    key={item.id + i}
                    className="target-panel-hover"
                    style={{ cursor: 'help' }}
                  >
                    {item.name}{i < defensiveItems.length - 1 ? ',' : ''}
                    {item.description && (
                      <span className="target-panel-tip" style={{ ...styles.tip, borderColor: 'rgba(240, 192, 64, 0.5)' }}>
                        <span style={{ ...styles.tipTitle, color: '#f0c040' }}>{item.name}</span>
                        <span style={styles.tipBody}>{item.description}</span>
                      </span>
                    )}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      </div>

      {loadout.passives.length > 0 && (
        <div style={styles.passiveSection}>
          <div style={styles.sectionLabel}>PASSIVES</div>
          {loadout.passives.map(p => (
            <div key={p.key} className="target-panel-hover" style={styles.passiveRow}>
              <span style={styles.passiveDot} />
              <span style={styles.passiveName}>{p.name}</span>
              {p.description && (
                <span className="target-panel-tip" style={{ ...styles.tip, borderColor: 'rgba(255, 136, 204, 0.5)' }}>
                  <span style={{ ...styles.tipTitle, color: '#ff88cc' }}>{p.name}</span>
                  <span style={styles.tipBody}>{p.description}</span>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={styles.deckSection}>
        <div style={styles.sectionLabel}>DECK ({fullDeck.length})</div>
        <div style={styles.deckRow}>
          {orderedCategories.map(cat => {
            const n = counts[cat.key] ?? 0;
            if (n === 0) return null;
            return (
              <div key={cat.key} style={styles.deckChip}>
                <span style={{ ...styles.deckChipDot, background: cat.color }} />
                <span style={styles.deckChipLabel}>{cat.label[0]}</span>
                <span style={styles.deckChipCount}>×{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      <BeliefBar confidence={agent.belief.confidence} />
    </div>
  );
}

// --- Styles ---

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: 'rgba(8, 12, 20, 0.92)',
    border: '1px solid rgba(255, 100, 100, 0.25)',
    borderRadius: '6px',
    padding: '10px 12px',
    backdropFilter: 'blur(4px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
    width: '100%',
    fontFamily: theme.fonts.mono,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    paddingBottom: '4px',
  },
  headerLabel: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#ff6464',
  },
  archetypeTag: {
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#888',
  },

  // --- Unknown state ---
  unknownBody: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  silhouette: {
    width: '44px',
    height: '44px',
    borderRadius: '4px',
    background: 'rgba(255, 100, 100, 0.08)',
    border: '1px dashed rgba(255, 100, 100, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: 'rgba(255, 100, 100, 0.4)',
    fontWeight: 700,
    flexShrink: 0,
  },
  unknownHint: {
    fontSize: '9px',
    color: '#777',
    lineHeight: '1.4',
  },

  // --- Revealed state ---
  revealedBody: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
  },
  faceWrap: {
    width: '52px',
    height: '52px',
    borderRadius: '3px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 100, 100, 0.35)',
    flexShrink: 0,
    background: '#111',
  },
  faceImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'grayscale(0.3) contrast(1.1)',
  },
  infoCol: {
    flex: 1,
    minWidth: 0,
  },
  codename: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#ff6464',
    marginBottom: '4px',
    letterSpacing: '1px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  kvRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    marginBottom: '2px',
  },
  kvLabel: {
    fontSize: '7px',
    color: '#666',
    letterSpacing: '1px',
    width: '26px',
    flexShrink: 0,
  },
  kvValue: {
    fontSize: '9px',
    fontWeight: 600,
  },

  // --- Passives section ---
  passiveSection: {
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  sectionLabel: {
    fontSize: '7px',
    color: '#666',
    letterSpacing: '1.5px',
    marginBottom: '3px',
  },
  passiveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '2px',
    cursor: 'help',
  },
  passiveDot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: '#ff88cc',
    flexShrink: 0,
  },
  passiveName: {
    fontSize: '9px',
    color: '#ff88cc',
    fontWeight: 600,
  },

  // --- Deck section ---
  deckSection: {
    marginTop: '6px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  deckRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  deckChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    padding: '2px 5px',
    background: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '3px',
  },
  deckChipDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
  },
  deckChipLabel: {
    fontSize: '8px',
    color: '#aaa',
    fontWeight: 700,
  },
  deckChipCount: {
    fontSize: '8px',
    color: '#fff',
    fontWeight: 700,
  },

  // --- Belief bar (agent's confidence in player position) ---
  beliefSection: {
    position: 'relative' as const,
    marginTop: '8px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    cursor: 'help',
  },
  // Tooltip for the belief bar. Anchored to the RIGHT of the TargetPanel
  // (left: 100% + margin) since the panel sits against the right edge of
  // the left HUD column — opening rightward clears the map viewport
  // cleanly. Rendered conditionally via React state in BeliefBar, so it
  // doesn't need the .target-panel-hover :hover CSS rule used elsewhere.
  beliefTip: {
    position: 'absolute' as const,
    left: 'calc(100% + 8px)',
    bottom: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '5px',
    padding: '8px 10px',
    minWidth: '200px',
    maxWidth: '260px',
    zIndex: 100,
    pointerEvents: 'none' as const,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'normal' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  beliefRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '3px',
  },
  beliefLabel: {
    fontSize: '8px',
    letterSpacing: '2px',
    color: theme.colors.textDim,
    fontWeight: 700,
  },
  beliefPct: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '1px',
  },
  beliefTrack: {
    width: '100%',
    height: '4px',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  beliefFill: {
    height: '100%',
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },

  // --- Hover tooltip (used for weapon, items, passives) ---
  // Anchored relative to the hovered element, floating to the LEFT of the
  // panel so tooltips don't overflow the map viewport's right edge (the
  // panel itself sits against that edge).
  //
  // NOTE: deliberately no `display` / `flex-direction` / `gap` here.
  // Those live in global.css under the `.target-panel-hover:hover
  // .target-panel-tip` selector so the "hidden by default" CSS rule
  // wins. An inline `display: flex` always beats external CSS's
  // `display: none` — which is exactly what caused the "dossier
  // tooltips stuck visible in the lower-left" regression after the
  // safehouse modal closed. Visual flex layout is applied via CSS
  // only on hover.
  tip: {
    position: 'absolute',
    right: 'calc(100% + 8px)',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(8, 12, 20, 0.98)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '5px',
    padding: '8px 10px',
    minWidth: '180px',
    maxWidth: '240px',
    zIndex: 100,
    pointerEvents: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'normal',
  },
  tipTitle: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1px',
  },
  tipBody: {
    fontSize: '9px',
    color: theme.colors.textPrimary,
    lineHeight: '1.4',
  },
};
